import React from 'react';
import { useModal } from 'react-modal-hook';
import { AlertContent, ModalFactory } from 'components';
import { IFormValues } from 'types';
import { AddButton, TipCalculationsContainer } from '../../styles';
import { CreateDefaultPool } from '../components/CreateDefaultPool';
import { PermissionTooltip, TipCalculationsDescription } from '../components/styles';
import { TipShareItem } from '../components/TipShareItem';

interface IDefaultPoolFormProps {
  siteId: string;
  activeRules?: IFormValues;
  isLoading?: boolean;
  onConfirm: (values: IFormValues, onSuccess?: () => void, successMessage?: string) => void;
  editPermission?: boolean;
  tabNav: React.ReactNode;
}

const textForDefaultPool = `Use the Default Pool to pool tips on checks that can't be processed with normal rules. 
                            Select “Include tips without timecard entries” to include checks closed by users that don't clock in to the POS. 
                            List other jobs that only occasionally collect tips that need to be shared to other employees.`;

const DefaultPoolForm: React.FC<IDefaultPoolFormProps> = ({ siteId, activeRules, isLoading, onConfirm, editPermission, tabNav }) => {
  const currentDefaultPool = activeRules.rules.find((r) => r.default);

  const [showCreateDefaultPool, hideCreateDefaultPool] = useModal(
    () => <CreateDefaultPool activeRules={activeRules} currentDefaultPool={currentDefaultPool} onConfirm={onConfirm} siteId={siteId} isLoading={isLoading} hideModal={hideCreateDefaultPool} />,
    [currentDefaultPool, isLoading, onConfirm]
  );

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    const onDelete = () => {
      const filteredRules = activeRules.rules.filter((rule) => rule.id !== currentDefaultPool.id);
      onConfirm({ ...activeRules, rules: filteredRules }, hideDeleteModal);
    };

    return (
      <ModalFactory customModal onClose={hideDeleteModal} isActive>
        <AlertContent
          title="Remove Default Pool?"
          message="This will delete the settings of the Default Pool"
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          onClose={hideDeleteModal}
          onLeftAction={hideDeleteModal}
          onRightAction={onDelete}
          isLoading={isLoading}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    );
  }, [activeRules, currentDefaultPool, isLoading, onConfirm]);

  return (
    <TipCalculationsContainer data-cy="default-pool-form">
      {tabNav}
      <TipCalculationsDescription size="xsmall">{textForDefaultPool}</TipCalculationsDescription>

      {currentDefaultPool ? (
        <TipShareItem tipShare={currentDefaultPool} onEdit={showCreateDefaultPool} onDelete={showDeleteModal} disabled={!editPermission} alwaysVisible />
      ) : (
        <>
          <AddButton appearance="white" icon="Plus" label="Create Default Pool" data-cy="create-default-pool-button" onClick={showCreateDefaultPool} disabled={!editPermission} tooltipId="no-edit-permission" />
          {!editPermission && <PermissionTooltip type="edit" />}
        </>
      )}
    </TipCalculationsContainer>
  );
};

export default DefaultPoolForm;
